// src/pages/PricingPage.js
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

const PricingPage = () => {
  const [currency, setCurrency] = useState({
    symbol: "$",
    code: "USD",
    rates: { free: 0, pro: 19, enterprise: "Custom" },
  });
  const [location, setLocation] = useState("");

  // Currency mapping based on common regions
  const currencyMap = useMemo(() => ({
    US: {
      symbol: "$",
      code: "USD",
      rates: { free: 0, pro: 19, enterprise: "Custom" },
    },
    GB: {
      symbol: "£",
      code: "GBP",
      rates: { free: 0, pro: 15, enterprise: "Custom" },
    },
    CA: {
      symbol: "C$",
      code: "CAD",
      rates: { free: 0, pro: 25, enterprise: "Custom" },
    },
    AU: {
      symbol: "A$",
      code: "AUD",
      rates: { free: 0, pro: 28, enterprise: "Custom" },
    },
    IN: {
      symbol: "₹",
      code: "INR",
      rates: { free: 0, pro: 1499, enterprise: "Custom" },
    },
    NG: {
      symbol: "₦",
      code: "NGN",
      rates: { free: 0, pro: 8999, enterprise: "Custom" },
    },
    EU: {
      symbol: "€",
      code: "EUR",
      rates: { free: 0, pro: 17, enterprise: "Custom" },
    },
    DEFAULT: {
      symbol: "$",
      code: "USD",
      rates: { free: 0, pro: 19, enterprise: "Custom" },
    },
  }), []);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const countryCode = data.country_code;
        setLocation(data.country_name || "");

        let selectedCurrency = currencyMap.DEFAULT;
        if (countryCode) {
            if (currencyMap[countryCode]) {
                 selectedCurrency = currencyMap[countryCode];
            } else if (["DE", "FR", "IT", "ES", "NL", "BE", "AT", "PT", "IE", "FI", "GR", "LU", "CY", "MT", "SI", "SK", "EE", "LV", "LT"].includes(countryCode)) { // Broader EU countries
                 selectedCurrency = currencyMap.EU;
            }
        }
        setCurrency(selectedCurrency);
      } catch (error) {
        console.warn("Location detection failed or API error, using default USD:", error);
        setCurrency(currencyMap.DEFAULT);
        setLocation(""); // Clear location if detection fails
      }
    };

    detectLocation();
  }, [currencyMap]);


  const pricingTiers = useMemo(() => [
    {
      id: "free",
      name: "Free Tier",
      price: currency.rates.free,
      frequency: "/month",
      description:
        "Perfect for getting started with Practap's core features.",
      features: [
        "Access to basic interview simulations",
        "Limited English practice sessions",
        "Community support",
        "Basic AI Resume analysis",
        "Standard pronunciation feedback",
      ],
      cta: "Get Started Free",
      link: "/register",
      isPopular: false, // Popularity should ideally be dynamic or based on actual data
      isEnabled: true,
      comingSoon: false,
    },
    {
      id: "pro",
      name: "Pro Plan",
      price: currency.rates.pro,
      frequency: "/month",
      description:
        "Unlock the full power of Practap for comprehensive preparation.",
      features: [
        "Everything in Free Tier",
        "Unlimited interview simulations",
        "Advanced English fluency feedback",
        "Personalized technical assessments (QA)",
        "Industry-specific scenarios (QA)",
        "Priority support",
        "Unlimited AI Resume analyses",
      ],
      cta: "Choose Pro",
      link: "/register?plan=pro",
      isPopular: true, // This will be conditionally rendered if not coming soon
      isEnabled: false,
      comingSoon: true,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "Custom",
      frequency: "",
      description: "Tailored solutions for teams and organizations.",
      features: [
        "Everything in Pro Plan",
        "Team dashboards and analytics",
        "Custom integration options",
        "Dedicated account manager",
        "Volume discounts",
        "Custom scenarios & question banks",
      ],
      cta: "Contact Sales",
      link: "/contact",
      isPopular: false,
      isEnabled: false,
      comingSoon: true,
    },
  ], [currency.rates]);

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <section aria-labelledby="pricing-hero-heading" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            {location && (
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-slate-700/50 border border-slate-600/70 text-sky-300 text-sm font-medium mb-8">
                <svg
                  aria-hidden="true"
                  className="w-4 h-4 mr-2 text-sky-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                Pricing for {location}
              </div>
            )}
             {!location && (
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-slate-700/50 border border-slate-600/70 text-slate-200 text-sm font-medium mb-8">
                Transparent Pricing
              </div>
            )}

            <h1 id="pricing-hero-heading" className="text-5xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              Find Your Plan
            </h1>
            <h2 className="text-4xl lg:text-5xl font-semibold text-slate-100 mb-8 leading-tight">
                Flexible options for everyone.
            </h2>

            <p className="text-xl text-slate-200 max-w-3xl mx-auto mb-12 leading-relaxed">
              Whether you're starting out, aiming for pro-level skills, or need a solution for your team, Practap has a plan that fits.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section aria-labelledby="pricing-tiers-heading" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="pricing-tiers-heading" className="sr-only">Pricing Tiers</h2>
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative flex flex-col bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 transform hover:scale-105 ${
                  tier.isPopular && !tier.comingSoon
                    ? "border-purple-500/50 ring-2 ring-purple-500/30 shadow-2xl shadow-purple-500/20"
                    : "border-slate-700/50 hover:border-blue-500/50"
                } ${!tier.isEnabled && !tier.comingSoon ? "opacity-60" : ""} ${tier.comingSoon ? "opacity-80" : ""}`}
              >
                {/* Popular Badge - only if isPopular and NOT comingSoon */}
                {tier.isPopular && !tier.comingSoon && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                {/* Coming Soon Badge */}
                {tier.comingSoon && (
                  <div className="absolute top-5 right-5">
                    <div className="bg-yellow-600/60 border border-yellow-700/70 text-yellow-100 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {tier.name}
                  </h3>
                  <p className="text-slate-300 text-sm mb-6 min-h-[40px]">
                    {tier.description}
                  </p>

                  <div className="mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-white">
                        {typeof tier.price === "number" ? currency.symbol : ""}
                        {tier.price}
                      </span>
                      {tier.frequency && (
                        <span className="text-slate-300 text-lg ml-2">
                          {tier.frequency}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <svg
                        aria-hidden="true"
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 mr-3 ${
                          tier.isPopular && !tier.comingSoon ? "text-purple-400" : "text-sky-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-slate-200 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <div className="mt-auto pt-4">
                  {tier.isEnabled ? (
                    <Link
                      to={tier.link}
                      className={`block w-full px-8 py-4 font-semibold text-center rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 ${
                        tier.isPopular && !tier.comingSoon
                          ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                          : "bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20"
                      }`}
                    >
                      {tier.cta}
                    </Link>
                  ) : (
                    <button
                      disabled
                      className="block w-full px-8 py-4 font-semibold text-center bg-slate-700/60 text-slate-400 border border-slate-600/80 rounded-xl cursor-not-allowed"
                    >
                      {tier.comingSoon ? "Coming Soon" : "Not Available"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section aria-labelledby="faq-heading" className="py-20 bg-slate-800/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="faq-heading" className="text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-200">
              Your questions, answered.
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "Is the free tier really free forever?",
                answer:
                  "Yes! Our free tier is completely free with no hidden costs. You can use it as long as you want with the included features and limits.",
              },
              {
                question:
                  "When will the Pro and Enterprise plans be available?",
                answer:
                  "We're working hard to launch our premium plans. Pro and Enterprise features will be available soon. Sign up to be notified!",
              },
              {
                question: "Do you offer refunds for paid plans?",
                answer:
                  "Once our paid plans launch, we'll offer a 30-day money-back guarantee if you're not satisfied with your experience.",
              },
              {
                question: "Can I change my plan later?",
                answer:
                  "Absolutely! You'll be able to upgrade or downgrade your plan at any time once our paid tiers are available.",
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-purple-500/50 transition-colors duration-300"
              >
                <h3 className="text-xl font-semibold text-white mb-3">
                  {faq.question}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section aria-labelledby="final-cta-heading" className="py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-slate-800 via-slate-800/80 to-slate-800 backdrop-blur-sm rounded-3xl p-12 border border-blue-500/20">
            <h2 id="final-cta-heading" className="text-4xl font-bold text-white mb-6">
              🚀 Ready to Elevate Your Skills?
            </h2>
            <p className="text-xl text-slate-200 mb-10 leading-relaxed">
              Join Practap today. Start with our free plan or explore options to best suit your journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link
                to="/register"
                className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center"
                >
                👉 Sign Up Free
                  <svg aria-hidden="true" className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path></svg>
                </Link>
            </div>
            <p className="text-slate-300 text-sm mt-6">
              No credit card required for the free tier.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PricingPage;
